/**
 * Pure helpers for the Connect Splyt page (/connect/:requestId). No Firebase,
 * no React, no fetch, so connectHelpers.test.js covers them without mocks.
 */

export const DEFAULT_CLIENT_NAME = 'the assistant';

export const STEP = Object.freeze({
  PHONE: 'phone',
  CODE: 'code',
  CONSENT: 'consent',
});

/**
 * The API origin. Empty in production: Firebase Hosting rewrites /oauth/** to
 * the mcp function on the same origin. Locally it is the emulator URL.
 * @param {string | undefined} raw
 */
export function apiBase(raw = process.env.REACT_APP_MCP_API_BASE) {
  return (typeof raw === 'string' ? raw : '').trim().replace(/\/+$/, '');
}

/** @param {string} base */
export function buildApiUrls(base) {
  return {
    request: (requestId) => `${base}/oauth/request/${encodeURIComponent(requestId)}`,
    approve: `${base}/oauth/approve`,
    deny: `${base}/oauth/deny`,
  };
}

/**
 * Normalize GET /oauth/request/:id. Returns null when the payload is not a
 * request at all, so the page shows the expired state instead of a blank card.
 * @param {unknown} payload
 */
export function parseConnectRequest(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const { requestId, clientName, redirectHost, scopes, expiresAt } = payload;
  if (typeof requestId !== 'string' || !requestId) return null;

  const cleanScopes = (Array.isArray(scopes) ? scopes : [])
    .filter((scope) => scope && typeof scope === 'object')
    .map((scope) => ({
      id: typeof scope.id === 'string' ? scope.id : '',
      description: typeof scope.description === 'string' ? scope.description.trim() : '',
    }))
    .filter((scope) => scope.description);

  return {
    requestId,
    clientName: typeof clientName === 'string' && clientName.trim() ? clientName.trim() : '',
    redirectHost: typeof redirectHost === 'string' ? redirectHost.trim() : '',
    scopes: cleanScopes,
    expiresAt: typeof expiresAt === 'number' && Number.isFinite(expiresAt) ? expiresAt : null,
  };
}

/**
 * @param {{ expiresAt: number | null } | null} request
 * @param {number} [now]
 */
export function isRequestExpired(request, now = Date.now()) {
  if (!request) return true;
  if (request.expiresAt === null) return false;
  return request.expiresAt <= now;
}

/**
 * A real (phone) session skips sign in. Anonymous receipt guests and signed
 * out visitors start at the phone step.
 * @param {{ uid?: string, isAnonymous?: boolean } | null | undefined} user
 */
export function firstStepForUser(user) {
  if (user && user.uid && !user.isAnonymous) return STEP.CONSENT;
  return STEP.PHONE;
}

/** @param {string | null | undefined} clientName */
export function assistantName(clientName) {
  const trimmed = (typeof clientName === 'string' ? clientName : '').trim();
  return trimmed || DEFAULT_CLIENT_NAME;
}

/** @param {string | null | undefined} clientName */
export function expiredMessage(clientName) {
  return `This connection request has expired. Go back to ${assistantName(clientName)} and try again.`;
}

const GENERIC_ERROR = "Couldn't reach Splyt. Check your connection and try again.";

/**
 * The sentence the error box shows for a failed approve or deny call. The
 * server's own message wins when it sent one; otherwise the status decides.
 * @param {number | null | undefined} status
 * @param {unknown} body parsed JSON body, when any
 * @param {string | null | undefined} clientName
 */
export function connectErrorMessage(status, body, clientName) {
  const serverMessage = body && typeof body === 'object' && typeof body.message === 'string'
    ? body.message.trim()
    : '';
  if (status === 404 || status === 410) return expiredMessage(clientName);
  if (serverMessage) return serverMessage;
  if (status === 401) return 'Your sign in ended. Refresh this page and try again.';
  if (status === 403) return 'This request was made for a different Splyt account.';
  return GENERIC_ERROR;
}

/** True when the status means the request is gone for good. */
export function isGoneStatus(status) {
  return status === 404 || status === 410;
}

/**
 * The redirect the server hands back after approve or deny. Only an absolute
 * http(s) URL is followed; anything else reads as '' and shows an error.
 * @param {unknown} payload
 */
export function parseRedirectUrl(payload) {
  const url = payload && typeof payload === 'object' && typeof payload.redirectUrl === 'string'
    ? payload.redirectUrl.trim()
    : '';
  return /^https?:\/\//i.test(url) ? url : '';
}

/** US display format for up to 10 digits. */
export function formatUsPhone(digits) {
  const clean = (typeof digits === 'string' ? digits : '').replace(/\D/g, '').slice(0, 10);
  if (clean.length <= 3) return clean;
  if (clean.length <= 6) return `(${clean.slice(0, 3)}) ${clean.slice(3)}`;
  return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6)}`;
}

/** E.164 for a 10 digit US number, or '' when it is not one. */
export function toUsE164(digits) {
  const clean = (typeof digits === 'string' ? digits : '').replace(/\D/g, '');
  return clean.length === 10 ? `+1${clean}` : '';
}

/** "(555) 555-5555" from a signed in user's +1 number, else the raw value. */
export function displayPhoneFor(e164) {
  const raw = typeof e164 === 'string' ? e164 : '';
  const match = raw.match(/^\+1(\d{10})$/);
  return match ? formatUsPhone(match[1]) : raw;
}

/** M:SS for the resend countdown. */
export function formatCountdown(seconds) {
  const safe = Math.max(0, Math.floor(seconds || 0));
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
